/**
 * Utility functions for notification handling, private browsing detection,
 * and localStorage management for the notification permission modal.
 */

// Constants for localStorage keys
export const NOTIFICATION_STORAGE_KEYS = {
  MODAL_DISMISSED_DESKTOP: 'notification-modal-dismissed-desktop',
  MODAL_DISMISSED_SESSION: 'notification-modal-dismissed-session',
} as const;

/**
 * Detects if the browser is running in private/incognito mode.
 * Uses multiple detection methods for cross-browser compatibility.
 */
export const isPrivateBrowsing = async (): Promise<boolean> => {
  try {
    // Method 1: Test localStorage accessibility
    // In some browsers, private mode restricts localStorage
    try {
      const testKey = '__private_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (e) {
      return true; // localStorage not available, likely private mode
    }

    // Method 2: Check storage quota (Chrome, Edge)
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        // In private mode, quota is typically very low (e.g., 120MB)
        // Normal mode usually has much higher quota (e.g., several GB)
        if (estimate.quota && estimate.quota < 200 * 1024 * 1024) { // Less than 200MB
          return true;
        }
      } catch (e) {
        // If storage.estimate() fails, continue with other checks
      }
    }

    // Method 3: Check for Safari private mode
    // Safari in private mode has limited indexedDB
    if ('indexedDB' in window) {
      try {
        const db = await new Promise<IDBDatabase>((resolve, reject) => {
          const request = indexedDB.open('__private_test__', 1);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
        
        db.close();
        indexedDB.deleteDatabase('__private_test__');
      } catch (e) {
        return true; // IndexedDB restricted, likely private mode
      }
    }

    // Method 4: Check for Firefox private mode
    // Firefox private mode doesn't allow writing to localStorage in some versions
    try {
      const storage = window.localStorage;
      const testKey = '__firefox_private_test__';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
    } catch (e) {
      return true;
    }

    return false; // Not in private mode
  } catch (error) {
    console.warn('Error detecting private browsing mode:', error);
    return false; // Default to not private if detection fails
  }
};

/**
 * Enhanced device detection with multiple methods for accuracy.
 * Detects mobile, tablet, and desktop devices.
 */
export const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = [
    'android', 'iphone', 'ipod', 'blackberry', 'windows phone',
    'mobile'
  ];
  
  const isMobileUserAgent = mobileKeywords.some(keyword => 
    userAgent.includes(keyword)
  );
  
  // Also check screen size as backup
  const isSmallScreen = window.innerWidth <= 768;
  
  // Check for touch capability
  const hasTouchScreen = 'ontouchstart' in window || 
    navigator.maxTouchPoints > 0 || 
    (navigator as any).msMaxTouchPoints > 0;
  
  return isMobileUserAgent || (isSmallScreen && hasTouchScreen);
};

/**
 * Detects if the device is a tablet (including iPad).
 */
export const isTabletDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const tabletKeywords = ['ipad', 'tablet', 'kindle', 'playbook', 'silk'];
  
  const isTabletUserAgent = tabletKeywords.some(keyword => 
    userAgent.includes(keyword)
  );
  
  // Check for medium screen size with touch
  const isMediumScreen = window.innerWidth > 768 && window.innerWidth <= 1024;
  const hasTouchScreen = 'ontouchstart' in window || 
    navigator.maxTouchPoints > 0 || 
    (navigator as any).msMaxTouchPoints > 0;
  
  return isTabletUserAgent || (isMediumScreen && hasTouchScreen);
};

/**
 * Detects if the device is a desktop computer.
 * More reliable than checking for !mobile as it considers tablets separately.
 */
export const isDesktopDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Explicit desktop indicators
  const desktopKeywords = [
    'windows nt', 'mac os x', 'linux', 'x11', 'cros'
  ];
  
  const isDesktopUserAgent = desktopKeywords.some(keyword => 
    userAgent.includes(keyword)
  );
  
  // Large screen without mobile/tablet indicators
  const isLargeScreen = window.innerWidth > 1024;
  const hasNoTouch = !('ontouchstart' in window) && 
    navigator.maxTouchPoints === 0 && 
    (navigator as any).msMaxTouchPoints === 0;
  
  // Not mobile and not tablet
  const notMobileOrTablet = !isMobileDevice() && !isTabletDevice();
  
  return isDesktopUserAgent || (isLargeScreen && hasNoTouch) || notMobileOrTablet;
};

/**
 * Gets detailed device information for debugging and logging.
 */
export const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    screenSize: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    touchCapabilities: {
      hasOntouchstart: 'ontouchstart' in window,
      maxTouchPoints: navigator.maxTouchPoints,
      msMaxTouchPoints: (navigator as any).msMaxTouchPoints || 0
    },
    isMobile: isMobileDevice(),
    isTablet: isTabletDevice(), 
    isDesktop: isDesktopDevice()
  };
};

/**
 * Determines if notifications should be allowed based on device type.
 * Desktop devices should not show intrusive notification popups.
 */
export const shouldAllowNotifications = (): boolean => {
  const deviceInfo = getDeviceInfo();
  
  // Allow notifications on mobile devices
  if (deviceInfo.isMobile) {
    return true;
  }
  
  // Allow notifications on tablets (user preference - can be changed)
  if (deviceInfo.isTablet) {
    return true;
  }
  
  // Prevent notifications on desktop devices
  if (deviceInfo.isDesktop) {
    return false;
  }
  
  // Default to not allowing notifications if device type is unclear
  return false;
};

/**
 * Determines if the notification permission modal should be blocked
 * specifically for desktop devices.
 */
export const shouldBlockNotificationModal = (): boolean => {
  return isDesktopDevice();
};

/**
 * Checks if the notification permission modal was dismissed by the user.
 * Uses different storage strategies for desktop vs mobile.
 */
export const wasModalDismissed = (): boolean => {
  try {
    const isMobile = isMobileDevice();
    
    if (isMobile) {
      // For mobile, check session storage (only for current session)
      return sessionStorage.getItem(NOTIFICATION_STORAGE_KEYS.MODAL_DISMISSED_SESSION) === 'true';
    } else {
      // For desktop, check localStorage (persistent)
      return localStorage.getItem(NOTIFICATION_STORAGE_KEYS.MODAL_DISMISSED_DESKTOP) === 'true';
    }
  } catch (error) {
    console.warn('Error checking modal dismissal state:', error);
    return false; // Default to not dismissed if storage is unavailable
  }
};

/**
 * Marks the notification permission modal as dismissed.
 * Uses different storage strategies for desktop vs mobile.
 */
export const markModalAsDismissed = (): void => {
  try {
    const isMobile = isMobileDevice();
    
    if (isMobile) {
      // For mobile, use session storage (only for current session)
      sessionStorage.setItem(NOTIFICATION_STORAGE_KEYS.MODAL_DISMISSED_SESSION, 'true');
    } else {
      // For desktop, use localStorage (persistent)
      localStorage.setItem(NOTIFICATION_STORAGE_KEYS.MODAL_DISMISSED_DESKTOP, 'true');
    }
  } catch (error) {
    console.warn('Error marking modal as dismissed:', error);
  }
};

/**
 * Clears the modal dismissal state.
 * Useful for testing or when user explicitly wants to reset.
 */
export const clearModalDismissalState = (): void => {
  try {
    localStorage.removeItem(NOTIFICATION_STORAGE_KEYS.MODAL_DISMISSED_DESKTOP);
    sessionStorage.removeItem(NOTIFICATION_STORAGE_KEYS.MODAL_DISMISSED_SESSION);
  } catch (error) {
    console.warn('Error clearing modal dismissal state:', error);
  }
};

/**
 * Determines if the notification permission modal should be shown.
 * Takes into account all the business rules:
 * - Device type (desktop devices are blocked)
 * - Private browsing mode
 * - Current notification permission status
 * - Previous dismissal state
 * - Mobile vs desktop behavior
 */
export const shouldShowNotificationModal = async (
  notificationPermission: NotificationPermission,
  isNotificationSupported: boolean
): Promise<boolean> => {
  try {
    // Don't show if notifications are not supported
    if (!isNotificationSupported) {
      return false;
    }

    // Don't show if notifications are already granted
    if (notificationPermission === 'granted') {
      return false;
    }

    // BLOCK DESKTOP DEVICES - Don't show notification modal on desktop
    if (shouldBlockNotificationModal()) {
      console.log('🖥️ Blocking notification modal on desktop device');
      return false;
    }

    // Don't show in private/incognito mode
    const isPrivate = await isPrivateBrowsing();
    if (isPrivate) {
      console.log('🔕 Skipping notification modal in private browsing mode');
      return false;
    }

    // Check if user previously dismissed the modal
    const wasDismissed = wasModalDismissed();
    if (wasDismissed) {
      console.log('🔕 Notification modal was previously dismissed');
      return false;
    }

    // For mobile devices, only show if permission is 'default'
    // For tablets, show for both 'default' and 'denied' states
    const isMobile = isMobileDevice();
    if (isMobile && notificationPermission === 'denied') {
      return false;
    }

    return true;
  } catch (error) {
    console.warn('Error determining if notification modal should show:', error);
    return false; // Default to not showing if there's an error
  }
};

/**
 * Gets a user-friendly description of the current notification state.
 * Useful for debugging and logging.
 */
export const getNotificationStateDescription = (
  permission: NotificationPermission,
  isSupported: boolean
): string => {
  if (!isSupported) {
    return 'Notifications not supported in this browser';
  }

  switch (permission) {
    case 'granted':
      return 'Notifications are enabled';
    case 'denied':
      return 'Notifications are blocked';
    case 'default':
      return 'Notification permission not yet requested';
    default:
      return `Unknown notification state: ${permission}`;
  }
};